/** @format */

import React, { useState } from "react";
import {
    Container,
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Chip,
    styled,
    alpha,
    Grid,
} from "@mui/material";
import {
    Public,
    CheckCircle,
    Search,
    Payments,
    SupportAgent,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Footer, SEO } from "../allComponents/AllComponents";
import {
    SUPPORTED_COUNTRIES,
    CountryConfig,
} from "../../utility/stripeConnectCountries";

// --- Styled Components (matching Fees / Features patterns) ---

const PageContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    fontFamily: "'Inter', sans-serif",
}));

const HeroSection = styled(Box)(({ theme }) => ({
    textAlign: "center",
    marginBottom: theme.spacing(6),
    padding: theme.spacing(5, 4),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
    borderRadius: theme.spacing(2),
}));

const Section = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(6),
}));

const StatCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    textAlign: "center",
    transition: "all 0.3s ease",
    height: "100%",
    "&:hover": {
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
        transform: "translateY(-4px)",
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    marginLeft: "auto",
    marginRight: "auto",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "#fff",
}));

const CountryTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
}));

// --- Region groupings ---

const REGIONS: Record<string, string[]> = {
    "North America": ["US", "CA", "MX"],
    Europe: [
        "GB", "DE", "FR", "ES", "IT", "NL", "BE", "IE", "PT", "AT", "CH",
        "SE", "NO", "DK", "FI", "PL", "CZ", "HU", "RO", "BG", "HR", "SK",
        "SI", "EE", "LV", "LT", "CY", "MT", "LU", "GR", "LI", "GI",
        "AL", "BA", "IS", "MC", "MD", "MK", "RS", "AM", "TR", "IL",
    ],
    "Asia Pacific": [
        "AU", "NZ", "SG", "HK", "JP", "MY", "TH", "ID", "PH", "VN", "IN",
        "KR", "BN", "KH", "MO", "TW", "MN", "LK", "PK", "UZ",
    ],
    "Latin America": ["BR", "AR", "CL", "CO", "PE", "UY", "BO", "CR", "DO", "EC", "SV", "GT", "GY", "PA", "PY"],
    Caribbean: ["TT", "JM", "AG", "BS", "LC"],
    "Middle East": ["AE", "BH", "SA", "QA", "KW", "OM", "JO"],
    Africa: [
        "ZA", "NG", "GH", "KE", "CI", "SN", "EG", "MA", "TZ", "RW", "ET",
        "BJ", "BW", "GM", "MG", "MU", "NA", "TN",
    ],
};

const getRegion = (code: string): string => {
    for (const [region, codes] of Object.entries(REGIONS)) {
        if (codes.includes(code)) return region;
    }
    return "Other";
};

const REGION_ORDER = [
    "North America",
    "Europe",
    "Asia Pacific",
    "Latin America",
    "Caribbean",
    "Middle East",
    "Africa",
    "Other",
];

// --- Component ---

const SupportedCountries: React.FC = () => {
    const { t } = useTranslation("pages");
    const [searchQuery, setSearchQuery] = useState("");

    // Map region keys to translation keys
    const regionTranslationMap: Record<string, string> = {
        "North America": t("supportedCountries.regions.northAmerica"),
        "Europe": t("supportedCountries.regions.europe"),
        "Asia Pacific": t("supportedCountries.regions.asiaPacific"),
        "Latin America": t("supportedCountries.regions.latinAmerica"),
        "Caribbean": t("supportedCountries.regions.caribbean"),
        "Middle East": t("supportedCountries.regions.middleEast"),
        "Africa": t("supportedCountries.regions.africa"),
        "Other": t("supportedCountries.regions.other"),
    };

    const filteredCountries = SUPPORTED_COUNTRIES.filter(
        (country: CountryConfig) =>
            country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            country.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
            country.currency.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const groupedCountries = filteredCountries.reduce(
        (acc, country) => {
            const region = getRegion(country.code);
            if (!acc[region]) acc[region] = [];
            acc[region].push(country);
            return acc;
        },
        {} as Record<string, CountryConfig[]>,
    );

    // Sort countries alphabetically within each region
    for (const region of Object.keys(groupedCountries)) {
        groupedCountries[region].sort((a, b) => a.name.localeCompare(b.name));
    }

    const uniqueCurrencies = new Set(
        SUPPORTED_COUNTRIES.map((c: CountryConfig) => c.currency.toUpperCase()),
    );

    return (
        <React.Fragment>
            <SEO
                title={t("supportedCountries.seo.title")}
                description={t("supportedCountries.seo.description")}
                keywords="supported countries, FundsyFi countries, Stripe supported countries, international savings circles, global ROSCA, multi-currency support"
                url="/supported-countries"
            />
            <PageContainer maxWidth="md">
                {/* Hero Section */}
                <HeroSection>
                    <Typography
                        variant="h3"
                        gutterBottom
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                        }}>
                        {t("supportedCountries.hero.title")}
                    </Typography>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ maxWidth: 700, mx: "auto", lineHeight: 1.6 }}>
                        {t("supportedCountries.hero.subtitle")}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 2,
                            maxWidth: 600,
                            mx: "auto",
                            fontStyle: "italic",
                        }}>
                        {t("supportedCountries.hero.note")}
                    </Typography>
                </HeroSection>

                {/* Stats Cards */}
                <Section>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <StatCard elevation={0}>
                                <IconWrapper>
                                    <Public />
                                </IconWrapper>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {SUPPORTED_COUNTRIES.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("supportedCountries.stats.countriesSupported")}
                                </Typography>
                            </StatCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatCard elevation={0}>
                                <IconWrapper>
                                    <Payments />
                                </IconWrapper>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {uniqueCurrencies.size}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("supportedCountries.stats.currenciesAvailable")}
                                </Typography>
                            </StatCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatCard elevation={0}>
                                <IconWrapper>
                                    <CheckCircle />
                                </IconWrapper>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {Object.keys(REGIONS).length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("supportedCountries.stats.regionsCovered")}
                                </Typography>
                            </StatCard>
                        </Grid>
                    </Grid>
                </Section>

                {/* Search Bar */}
                <Section>
                    <TextField
                        fullWidth
                        placeholder={t("supportedCountries.search.placeholder")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                </Section>

                {/* Country Table grouped by region */}
                {REGION_ORDER.map((region) => {
                    const countries = groupedCountries[region];
                    if (!countries || countries.length === 0) return null;

                    return (
                        <Section key={region}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 600,
                                    mb: 1,
                                }}>
                                {regionTranslationMap[region] || region}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2 }}>
                                {countries.length}{" "}
                                {countries.length === 1
                                    ? t("supportedCountries.countryCount.singular")
                                    : t("supportedCountries.countryCount.plural")}
                            </Typography>
                            <CountryTableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                backgroundColor: (theme) =>
                                                    alpha(theme.palette.primary.main, 0.04),
                                            }}>
                                            <TableCell sx={{ fontWeight: 600 }}>{t("supportedCountries.table.country")}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t("supportedCountries.table.code")}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{t("supportedCountries.table.currency")}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }} align="center">
                                                {t("supportedCountries.table.status")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {countries.map((country: CountryConfig) => (
                                            <TableRow
                                                key={country.code}
                                                sx={{
                                                    "&:last-child td, &:last-child th": { border: 0 },
                                                    "&:hover": {
                                                        backgroundColor: (theme) =>
                                                            alpha(theme.palette.primary.main, 0.02),
                                                    },
                                                }}>
                                                <TableCell>
                                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                        {country.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={country.code}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={country.currency.toUpperCase()}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <CheckCircle
                                                        color="success"
                                                        sx={{ fontSize: 20 }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CountryTableContainer>
                        </Section>
                    );
                })}

                {/* No results message */}
                {filteredCountries.length === 0 && (
                    <Section>
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {t("supportedCountries.noResults.title")} &quot;{searchQuery}&quot;
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t("supportedCountries.noResults.subtitle")}
                            </Typography>
                        </Box>
                    </Section>
                )}

                {/* "Don't see your country?" CTA */}
                <Section>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            textAlign: "center",
                            background: (theme) =>
                                `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.primary.main, 0.07)} 100%)`,
                        }}>
                        <SupportAgent color="primary" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography
                            variant="h5"
                            gutterBottom
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 600,
                            }}>
                            {t("supportedCountries.cta.title")}
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ maxWidth: 550, mx: "auto", mb: 2 }}>
                            {t("supportedCountries.cta.description")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("supportedCountries.cta.stayTuned")}
                        </Typography>
                    </Paper>
                </Section>
            </PageContainer>
            <Footer />
        </React.Fragment>
    );
};

export default SupportedCountries;
